// src/pages/DebtStudio/components/Reports/CapIReport.jsx
import React, { useMemo } from "react";
import SectionCard from "components/SectionCard";
import ExcelGrid from "components/ExcelGrid";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import ChartTooltip from "components/ChartTooltip";
import dayjs from "lib/dayjs";
import { computeCapI } from "pages/DebtStudio/lib/computeCapI";
import { makeNiceScaleZeroBased } from "lib/chartScale";

/**
 * CapI Report
 * ---------------------------------------------------------
 * Shows a CapI cashflow ledger (deposit, earnings credited on payment dates,
 * interest payments) with running balance.
 *
 * Chart: Line (balance) + Bars (Interest Outflows, Earnings Inflows)
 * Table: Event ledger (Deposit, Earnings, Interest Payment)
 *
 * NOTE:
 * - Prefer passing coupon-complete rows (e.g. scheduleForMath) for accuracy.
 * - Earnings convention:
 *   - Credited ON each included coupon payment date
 *   - Then the interest outflow occurs on that same date
 * - Earnings rate is fixed (form.capiInvestmentRate), treated as percent input.
 */
export default function CapIReport({ form = {}, rows = [] }) {
  const cleanMoney = (v) => {
    const n = Number(v || 0);
    return Math.abs(n) < 0.005 ? 0 : n;
  };

  const fmt2 = (v) =>
    v != null
      ? cleanMoney(v).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "";

  const safeIso = (d) => {
    const x = d ? dayjs(d) : null;
    return x && x.isValid() ? x.format("YYYY-MM-DD") : "";
  };

  const ratePctToDec = (x) => {
    // UI is percent. If user types 3.25 => 0.0325.
    // Still tolerate decimals (0.0325) by treating <= 1 as decimal.
    const n = Number(x);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n <= 1 ? n : n / 100;
  };

  const earningsYearBasisFromDayCount = (dayCount) => {
    const dc = String(dayCount || "").toUpperCase();
    if (dc.includes("ACT/360")) return 360;
    return 365;
  };

  const yearFracACT = (d0, d1, basis) => {
    const a = dayjs(d0);
    const b = dayjs(d1);
    if (!a.isValid() || !b.isValid()) return 0;
    const days = b.diff(a, "day");
    if (!Number.isFinite(days) || days <= 0) return 0;
    return days / (basis || 365);
  };

  /** ---------- Money formatting helpers (consistent w/ DS charts) ---------- */
  const formatKMB = (v) => {
    const a = Math.abs(v);
    if (a >= 1e9)
      return `${(v / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 })}B`;
    if (a >= 1e6)
      return `${(v / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
    if (a >= 1e3)
      return `${(v / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
    return (v || 0).toLocaleString();
  };

  const report = useMemo(() => {
    // Gate: only show for enabled CapI deals
    const dealType = form?.deal_type || form?.dealType;
    const capiEnabled = form?.capiEnabled ?? form?.capi_enabled ?? false;

    const capi = computeCapI({
      dealType,
      capiEnabled,
      capiDate: form?.capiDate ?? form?.capi_date ?? "",
      capiInvestmentRate:
        form?.capiInvestmentRate ?? form?.capi_investment_rate ?? 0,
      rows,
      form: {
        datedDate: form?.datedDate ?? form?.dated_date ?? null,
        deliveryDate: form?.deliveryDate ?? form?.delivery_date ?? null,
        firstCouponDate: form?.firstCouponDate ?? form?.first_coupon_date ?? null,
        finalMaturityDate:
          form?.finalMaturityDate ?? form?.final_maturity_date ?? null,
        couponFrequency:
          form?.couponFrequency ?? form?.coupon_frequency ?? "semiannual",
        dayCount: form?.dayCount ?? form?.day_count ?? "30/360",
      },
    });

    if (!capi?.enabled) return { capi, ledger: [], chartData: [], summary: null };

    const deliveryISO =
      safeIso(form?.deliveryDate ?? form?.delivery_date) ||
      safeIso(form?.datedDate ?? form?.dated_date) ||
      "";

    const rateDec = ratePctToDec(
      form?.capiInvestmentRate ?? form?.capi_investment_rate ?? 0
    );

    const yearBasis = earningsYearBasisFromDayCount(
      form?.dayCount ?? form?.day_count
    );

    const initialDeposit = Number(capi?.capiUse || 0);
    const flows = Array.isArray(capi?.flowsIncluded) ? capi.flowsIncluded : [];

    // Normalize flows to {date, interest} and sort
    const pays = flows
      .map((f) => ({
        date: safeIso(f?.date) || String(f?.date || ""),
        interest: Number(f?.interest || 0),
      }))
      .filter((x) => !!x.date)
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    // Build ledger rows:
    // - Deposit row at delivery
    // - For each payment date:
    //    (1) earnings inflow row (if any)
    //    (2) interest payment outflow row
    const data = [];

    if (deliveryISO && initialDeposit > 0) {
      data.push({
        date: deliveryISO,
        type: "Inflow",
        description: "Initial Cap-I Deposit",
        inflow: initialDeposit,
        outflow: 0,
        netChange: initialDeposit,
        _sortKey: 0,
      });
    }

    let bal = initialDeposit;
    let lastDate = deliveryISO || pays[0]?.date || "";

    let totalEarnings = 0;
    let minBalance = bal;

    for (const p of pays) {
      const payDate = p.date;

      // Earnings credited ON pay date, based on elapsed time since lastDate
      const yf = yearFracACT(lastDate, payDate, yearBasis);
      let earned = 0;
      if (rateDec > 0 && yf > 0 && bal > 0) {
        earned = bal * rateDec * yf;
      }

      earned = Number.isFinite(earned) ? earned : 0;
      if (earned > 0.0000001) {
        totalEarnings += earned;
        bal += earned;

        data.push({
          date: payDate,
          type: "Inflow",
          description: "Cap-I Earnings (estimated)",
          inflow: earned,
          outflow: 0,
          netChange: earned,
          _sortKey: 1,
        });
      }

      // Interest payment outflow ON pay date
      const payInt = Number(p.interest || 0);
      const out = Number.isFinite(payInt) && payInt > 0 ? payInt : 0;

      if (out > 0) {
        bal -= out;

        data.push({
          date: payDate,
          type: "Outflow",
          description: "Interest Payment",
          inflow: 0,
          outflow: out,
          netChange: -out,
          _sortKey: 2,
        });
      }

      if (bal < minBalance) minBalance = bal;
      lastDate = payDate;
    }

    // Sort + running balance for display (recompute deterministically)
    data.sort((a, b) => {
      const ad = a.date || "";
      const bd = b.date || "";
      if (ad !== bd) return ad < bd ? -1 : 1;
      const ak = a._sortKey ?? 9;
      const bk = b._sortKey ?? 9;
      return ak - bk;
    });

    let running = 0;

    const ledger = data.map((r) => {
      running = cleanMoney(running + (r.netChange || 0));
      minBalance = Math.min(minBalance, running);

      return {
        date: r.date,
        type: r.type,
        description: r.description,
        inflow: cleanMoney(r.inflow || 0),
        outflow: cleanMoney(r.outflow || 0),
        netChange: cleanMoney(r.netChange || 0),
        balance: running,
      };
    });

    const endBalance = ledger.length ? ledger[ledger.length - 1].balance : 0;

    // Aggregate for chart (one point per date)
    const map = new Map();

    for (const r of ledger) {
      const key = dayjs(r.date).isValid()
        ? dayjs(r.date).format("YYYY-MM-DD")
        : String(r.date);

      const existing = map.get(key) || {
        date: key,
        balance: 0,
        outflows: 0,
        earnings: 0,
      };

      // Latest balance for the date = last row's balance that day
      // CapI chart should never show negative, clamp to 0 for display safety.
      existing.balance = Math.max(0, Number(r.balance || 0));

      // Sum outflows and earnings by day (defensive clamp at 0)
      existing.outflows += Math.max(0, Number(r.outflow || 0));

      if (r.description && r.description.toLowerCase().includes("earnings")) {
        existing.earnings += Math.max(0, Number(r.inflow || 0));
      }

      map.set(key, existing);
    }

    const chartData = Array.from(map.values()).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    const summary = {
      cutoffDate: capi?.cutoffDate || "",
      initialDeposit: initialDeposit,
      totalInterestThroughCutoff: Number(capi?.capiInterestThroughDate || 0),
      totalEarnings: totalEarnings,
      endBalance: endBalance,
      minBalance: minBalance,
      ratePct:
        Number(form?.capiInvestmentRate ?? form?.capi_investment_rate ?? 0) || 0,
      earningsBasis: `ACT/${yearBasis}`,
      depositDate: deliveryISO || "",
    };

    return { capi, ledger, chartData, summary };
  }, [form, rows]);

  const { capi, ledger, chartData, summary } = report;

  const columns = [
    {
      key: "date",
      label: "Date",
      date: true,
      className: "numeric",
    },
    { key: "type", label: "Type" },
    { key: "description", label: "Description" },
    {
      key: "inflow",
      label: "Inflow ($)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
    {
      key: "outflow",
      label: "Outflow ($)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
    {
      key: "netChange",
      label: "Net Change ($)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
    {
      key: "balance",
      label: "Balance ($)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
  ];

  const maxVal = useMemo(() => {
    let m = 0;

    for (const d of chartData || []) {
      m = Math.max(
        m,
        Number(d.balance) || 0,
        Number(d.outflows) || 0,
        Number(d.earnings) || 0
      );
    }

    return m;
  }, [chartData]);

  // Zero-based axis with nice ticks (anchors at 0, adds headroom)
  const scale = useMemo(() => makeNiceScaleZeroBased(maxVal, 6, 1), [maxVal]);

  const yTickFormatter = (v) => `$${formatKMB(v)}`;

  const tooltipFormatter = (val, name) => [
    `$${Number(val).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
    name,
  ];

  if (!capi?.enabled || !ledger?.length) return null;

  return (
    <SectionCard title="Capitalized Interest">
      {/* ---------- Summary ---------- */}
      {summary ? (
        <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="bg-card border border-border rounded-xl p-3">
            <div className="text-muted text-sm">Initial Deposit</div>

            <div className="font-medium numeric">
              ${fmt2(summary.initialDeposit)}
            </div>

            <div className="text-muted text-sm mt-1">
              Deposit Date:{" "}
              <span className="numeric">
                {summary.depositDate || "—"}
              </span>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-3">
            <div className="text-muted text-sm">
              Total Capitalized Interest
            </div>

            <div className="font-medium numeric">
              ${fmt2(summary.totalInterestThroughCutoff)}
            </div>

            <div className="text-muted text-sm mt-1">
              Last Date:{" "}
              <span className="numeric">
                {summary.cutoffDate || "—"}
              </span>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-3">
            <div className="text-muted text-sm">
              Estimated Earnings
            </div>

            <div className="font-medium numeric">
              ${fmt2(summary.totalEarnings)}
            </div>

            <div className="text-muted text-sm mt-1">
              Rate: <span className="numeric">{summary.ratePct}%</span> ,
              Basis:{" "}
              <span className="numeric">
                {summary.earningsBasis}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {/* ---------- Chart ---------- */}
      <div className="h-96 bg-card border border-border rounded-xl p-4 mb-4 shadow-sm">
        <ResponsiveContainer>
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis dataKey="date" />

            <YAxis
              domain={[0, scale.niceMax]}
              ticks={scale.ticks}
              tickFormatter={yTickFormatter}
            />

            <Tooltip
              content={<ChartTooltip formatter={tooltipFormatter} />}
            />

            <Legend
              verticalAlign="bottom"
              align="center"
              height={18}
              wrapperStyle={{
                bottom: 0,
                fontSize: 13,
                lineHeight: "1em",
                marginTop: 0,
                padding: 0,
              }}
            />

            {/* Bars: Outflows (interest) and Earnings */}
            <Bar
              dataKey="outflows"
              fill="var(--chart-2)"
              name="Interest Outflows"
              barSize={18}
            />

            <Bar
              dataKey="earnings"
              fill="var(--chart-1)"
              name="Earnings (credited)"
              barSize={18}
            />

            {/* Line: Balance */}
            <Line
              type="monotone"
              dataKey="balance"
              stroke="var(--chart-3)"
              name="Cap-I Balance"
              dot={{ r: 3, fill: "var(--chart-3)" }}
              activeDot={{ r: 5 }}
              strokeWidth={3}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ---------- Table ---------- */}
      <ExcelGrid columns={columns} rows={ledger} />
    </SectionCard>
  );
}
